/* Client error reports (DOM-free queue, unit-tested). Uncaught errors and
   rejections are trimmed, de-duplicated, capped per session, kept in a
   small persistent buffer while offline / signed out, and handed to a
   sender (Firestore `errors` collection) when one is available. */
export interface ErrorReport {
  ts: number; page: string; layout: string | null; ua: string; build: string;
  message: string; stack: string; source: string;
}
export const MAX_PER_SESSION = 5;
export const MAX_PENDING = 20;
const MSG_MAX = 300, STACK_MAX = 2000;

export interface ReportStorage { getItem(k: string): string | null; setItem(k: string, v: string): void }
const KEY = 'playout:errors';

export function formatReport(e: { message?: unknown; stack?: unknown; source?: string },
                             ctx: { page: string; layout: string | null; ua: string; build: string; now?: number }): ErrorReport {
  const cut = (v: unknown, n: number) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, n);
  return { ts: ctx.now ?? Date.now(), page: ctx.page.slice(0, 200), layout: ctx.layout ? ctx.layout.slice(0, 100) : null,
           ua: ctx.ua.slice(0, 200), build: ctx.build.slice(0, 40),
           message: cut(e.message, MSG_MAX) || 'Unknown error', stack: cut(e.stack, STACK_MAX), source: (e.source || 'error').slice(0, 40) };
}

export class ReportQueue {
  private seen = new Set<string>();
  private count = 0;
  pending: ErrorReport[] = [];
  private storage: ReportStorage | null;
  constructor(storage: ReportStorage | null) {
    this.storage = storage;
    try { this.pending = JSON.parse(storage?.getItem(KEY) || '[]'); } catch { this.pending = []; }
    if (!Array.isArray(this.pending)) this.pending = [];
  }
  /** Queue a report unless it's a repeat or the session cap is hit. Returns whether it was queued. */
  add(r: ErrorReport): boolean {
    const key = r.message + '|' + r.source;
    if (this.seen.has(key) || this.count >= MAX_PER_SESSION) return false;
    this.seen.add(key); this.count++;
    this.pending.push(r);
    if (this.pending.length > MAX_PENDING) this.pending.splice(0, this.pending.length - MAX_PENDING);
    this.persist();
    return true;
  }
  /** Hand everything pending to `send`; what fails stays pending. */
  async flush(send: (r: ErrorReport) => Promise<void>): Promise<number> {
    const batch = this.pending.slice();
    let sent = 0;
    for (const r of batch) {
      try { await send(r); this.pending = this.pending.filter(x => x !== r); sent++; } catch { break; }
    }
    this.persist();
    return sent;
  }
  private persist(): void { try { this.storage?.setItem(KEY, JSON.stringify(this.pending)); } catch { /* full or private */ } }
}
